import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MapPin, Plus, AlertTriangle, ChevronDown, ChevronRight, MoreHorizontal, Lock, Unlock, ArrowRightLeft, Ban, GripVertical, User, Sparkles } from "lucide-react";
import { MOTIVOS_EXCLUSION, dedupeLineaIds } from "@/lib/repartoPropuesta";
import { resumenRequisitosParadas } from "@/lib/requisitosMontaje";
import { RequisitosChips } from "@/components/espacios/RequisitosForm";
import { MaterialBadge } from "@/components/MaterialBadge";

export const GRUPO_COLORS = [
  { cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200", hex: "#34d399" },
  { cls: "bg-cyan-500/15 border-cyan-500/40 text-cyan-200", hex: "#22d3ee" },
  { cls: "bg-amber-500/15 border-amber-500/40 text-amber-200", hex: "#fbbf24" },
  { cls: "bg-violet-500/15 border-violet-500/40 text-violet-200", hex: "#a78bfa" },
  { cls: "bg-rose-500/15 border-rose-500/40 text-rose-200", hex: "#fb7185" },
  { cls: "bg-blue-500/15 border-blue-500/40 text-blue-200", hex: "#60a5fa" },
];
export const colorGrupo = (i) => GRUPO_COLORS[i % GRUPO_COLORS.length];

function BarraCarga({ n, cap }) {
  if (!cap) return <span className="font-mono text-[11px]">{n}</span>;
  const pct = Math.min(100, Math.round((n / cap) * 100));
  const tone = pct >= 100 ? "bg-red-400" : pct >= 80 ? "bg-amber-400" : "bg-emerald-400";
  return (
    <div className="flex items-center gap-2" title={`Carga ${n}/${cap}`}>
      <div className="w-20 h-1.5 rounded-full bg-zinc-800 overflow-hidden"><div className={`h-full ${tone}`} style={{ width: `${pct}%` }} /></div>
      <span className="font-mono text-[11px]" data-testid={`carga-${n}-${cap}`}>{n}/{cap}</span>
    </div>
  );
}

/** Menú de acciones de una parada: Mover / Fijar / Excluir (funciona sin drag & drop). */
function MenuParada({ p, g, grupos, onMover, onFijar, onExcluir }) {
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState(MOTIVOS_EXCLUSION[0]);
  const otros = grupos.filter((x) => x.id !== g.id);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`parada-menu-${p.linea_id}`} className="p-1 rounded hover:bg-zinc-800/80 text-zinc-400 hover:text-zinc-100" title="Acciones">
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2 bg-zinc-950 border-zinc-800 text-zinc-100 text-xs space-y-1">
        <div className="text-[10px] uppercase text-zinc-500 px-1">Mover a ruta…</div>
        {otros.length === 0 && <div className="px-1 text-zinc-500 italic">No hay otras rutas</div>}
        {otros.map((x) => {
          const lleno = x.capacidad && x.paradas.length >= x.capacidad;
          return (
            <button key={x.id} type="button" disabled={lleno} data-testid={`mover-${p.linea_id}-${x.id}`}
              onClick={() => { onMover(p.linea_id, x.id); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
              <ArrowRightLeft className="w-3 h-3" /> {x.id} · {x.ciudad_principal || "—"} <span className="ml-auto font-mono opacity-70">{x.carga}</span>{lleno && " 🔒"}
            </button>
          );
        })}
        <div className="border-t border-zinc-800 my-1" />
        <button type="button" data-testid={`fijar-${p.linea_id}`} onClick={() => { onFijar(p.linea_id); setOpen(false); }}
          className="w-full text-left px-2 py-1.5 rounded hover:bg-zinc-800 flex items-center gap-2">
          {p.fijada ? <><Unlock className="w-3 h-3" /> Desfijar (permitir reoptimizar)</> : <><Lock className="w-3 h-3" /> Fijar en esta ruta</>}
        </button>
        <div className="border-t border-zinc-800 my-1" />
        <div className="text-[10px] uppercase text-zinc-500 px-1">Excluir de esta planificación</div>
        <Select value={motivo} onValueChange={setMotivo}>
          <SelectTrigger data-testid={`motivo-${p.linea_id}`} className="h-8 bg-zinc-900 border-zinc-800 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">{MOTIVOS_EXCLUSION.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <button type="button" data-testid={`excluir-${p.linea_id}`} onClick={() => { onExcluir(p.linea_id, motivo); setOpen(false); }}
          className="w-full text-left px-2 py-1.5 rounded hover:bg-rose-500/15 text-rose-300 flex items-center gap-2">
          <Ban className="w-3 h-3" /> Quitar de la propuesta
        </button>
        <div className="text-[10px] text-zinc-500 px-1">No modifica la petición ni la línea; sólo este borrador.</div>
      </PopoverContent>
    </Popover>
  );
}

function ParadaItem({ p, idx, g, grupos, fechaPlan, acciones, bloqueN }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: p.linea_id, data: { grupoId: g.id } });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 } : undefined;
  const maps = p.lat && p.lon ? `https://www.google.com/maps?q=${p.lat},${p.lon}` : null;
  const fechaDistinta = p.fecha_montaje && p.fecha_montaje !== fechaPlan;
  return (
    <li ref={setNodeRef} style={style} data-testid={`parada-${p.linea_id}`}
      className={`bg-zinc-950/40 rounded p-2 flex items-start gap-1.5 ${isDragging ? "opacity-80 ring-2 ring-amber-400 shadow-xl" : ""} ${p.fijada ? "border-l-2 border-amber-400" : ""}`}>
      <button type="button" {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing text-zinc-600 hover:text-zinc-300 mt-0.5 touch-none" title="Arrastrar a otra ruta" data-testid={`drag-${p.linea_id}`}>
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <span className="font-mono text-[10px] text-zinc-400 shrink-0 mt-0.5">#{idx + 1}</span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate flex items-center gap-1">
          {p.fijada && <Lock className="w-3 h-3 text-amber-300 shrink-0" title="Fijada" data-testid={`fijada-${p.linea_id}`} />}
          {p.oportunidad && <Sparkles className="w-3 h-3 text-amber-300 shrink-0" title="Añadida como oportunidad cercana" data-testid={`oportunidad-badge-${p.linea_id}`} />}
          <span className="truncate">{p.espacio_nombre || "—"}</span>
          {bloqueN > 1 && <span className="text-[9px] px-1 rounded bg-zinc-800 text-zinc-300 shrink-0" title="Paradas en el mismo destino">×{bloqueN}</span>}
          {p.situacion_fecha === "atrasado" && <span className="text-[9px] px-1 rounded bg-orange-500/20 text-orange-200 border border-orange-500/40 shrink-0" data-testid={`badge-atrasado-${p.linea_id}`}>ATRASADO</span>}
          {p.situacion_fecha === "sin_fecha" && <span className="text-[9px] px-1 rounded bg-zinc-700/60 text-zinc-200 border border-zinc-600 shrink-0" data-testid={`badge-sin-fecha-${p.linea_id}`}>SIN FECHA</span>}
          {p.referencia_cliente && <span className="font-mono text-[10px] opacity-70 shrink-0">Ref. {p.referencia_cliente}</span>}
          <RequisitosChips req={p.requisitos_montaje} testId={`parada-req-${p.linea_id}`} />
        </div>
        <div className="text-[10px] opacity-75 truncate">
          {p.cliente_nombre} · {p.peticion_codigo}{p.marca ? ` · ${p.marca}` : ""}
          {p.fecha_montaje && <span className={fechaDistinta ? " text-amber-300" : ""}> · 📅 {p.fecha_montaje}{p.fecha_origen === "fecha_montaje_prevista" ? " (cita)" : p.fecha_origen === "fecha_objetivo" ? " (objetivo)" : ""}</span>}
          {p.horas_estimadas ? <span> · ⏱ {p.horas_estimadas} h</span> : null}
        </div>
        <div className="text-[10px] opacity-60 truncate flex items-center gap-1.5">{p.direccion ? `${p.direccion} · ` : ""}{p.ciudad}
          {p.disponibilidad && <MaterialBadge disponibilidad={p.disponibilidad} compact testId={`parada-material-${p.linea_id}`} />}
        </div>
      </div>
      {maps && <a href={maps} target="_blank" rel="noreferrer" className="text-cyan-300 hover:text-cyan-200 shrink-0 mt-0.5" title="Ver en mapa"><MapPin className="w-3.5 h-3.5" /></a>}
      <MenuParada p={p} g={g} grupos={grupos} {...acciones} />
    </li>
  );
}

/** Tarjeta editable de una ruta sugerida (droppable). */
export default function GrupoCard({ g, i, grupos, fechaPlan, acciones, montadores, onMontador, onOportunidades }) {
  const [verParadas, setVerParadas] = useState(true);
  const { setNodeRef, isOver } = useDroppable({ id: g.id });
  const color = colorGrupo(i);
  const lleno = g.capacidad && g.paradas.length >= g.capacidad;
  const bloqueDe = (p) => g.bloques?.find((b) => b.key === (p.bloque_key || p.espacio_id))?.n || 1;
  const montadorVal = g.montador ? `${g.montador.usuario_id ? "u" : "e"}:${g.montador.usuario_id || g.montador.externo_id}` : "none";
  return (
    <div ref={setNodeRef} data-testid={`grupo-${g.id}`}
      className={`rounded-xl border-2 p-3 transition-shadow ${color.cls} ${isOver ? (lleno ? "ring-2 ring-red-400" : "ring-2 ring-white/70 shadow-xl") : ""}`}>
      <div className="sticky top-0 z-10 -mx-3 -mt-3 px-3 pt-3 pb-2 rounded-t-xl bg-zinc-950/70 backdrop-blur-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-base shrink-0 text-zinc-900" style={{ background: color.hex }}>{g.id}</div>
            <div className="min-w-0">
              <div className="font-bold text-sm truncate" data-testid={`grupo-${g.id}-zona`}>
                {g.ciudad_principal || g.provincia_principal || "Sin zona"}
                {g.ciudades?.length > 1 && <span className="opacity-60 font-normal"> +{g.ciudades.length - 1}</span>}
              </div>
              <div className="text-[11px] opacity-75" data-testid={`grupo-${g.id}-contadores`}>{g.n_paradas} trabajos{g.n_destinos != null && g.n_destinos !== g.n_paradas && <> en {g.n_destinos} destinos</>} · ~{g.distancia_km_estimada} km{g.horas_montaje_estimadas != null && <> · ~{g.horas_montaje_estimadas} h</>}</div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <BarraCarga n={g.n_paradas} cap={g.capacidad} />
            <div className="flex gap-1">
              {onOportunidades && (
                <button type="button" onClick={() => onOportunidades(g.id)} data-testid={`oportunidades-${g.id}`} title="Buscar trabajos pendientes cercanos al recorrido"
                  className="text-[10px] uppercase tracking-wider bg-zinc-900/80 hover:bg-zinc-900 text-amber-200 px-2 py-1 rounded inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Oportunidades
                </button>
              )}
              <Link to="/rutas/nueva" state={{ linea_ids: dedupeLineaIds(g.paradas.map((p) => p.linea_id)), montador: g.montador, fecha: fechaPlan, nombre: `Reparto ${fechaPlan} · ${g.ciudad_principal || g.id}` }}
                data-testid={`crear-ruta-${g.id}`} className="text-[10px] uppercase tracking-wider bg-zinc-900/80 hover:bg-zinc-900 text-zinc-100 px-2 py-1 rounded inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Crear ruta
              </Link>
            </div>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <User className="w-3.5 h-3.5 opacity-70 shrink-0" />
          <Select value={montadorVal} onValueChange={(v) => onMontador(g.id, v === "none" ? null : montadores.find((m) => m.value === v) || null)}>
            <SelectTrigger data-testid={`montador-${g.id}`} className="h-8 bg-zinc-950/60 border-zinc-800 text-xs"><SelectValue placeholder="Montador (opcional)" /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <SelectItem value="none">Sin asignar</SelectItem>
              {montadores.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {g.campanas?.length > 0 && (
        <div className="flex flex-wrap gap-1 my-2" data-testid={`grupo-${g.id}-campanas`}>
          {g.campanas.map((c) => <span key={c} className="text-[10px] font-mono bg-zinc-950/50 px-1.5 py-0.5 rounded">{c}</span>)}
        </div>
      )}
      {g.bloques?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2" data-testid={`grupo-${g.id}-bloques`}>
          {g.bloques.map((b) => <span key={b.key} className="text-[10px] bg-zinc-950/60 border border-zinc-700 px-1.5 py-0.5 rounded">📍 {b.label} · {b.n} montajes</span>)}
        </div>
      )}
      {(() => { const rq = resumenRequisitosParadas(g.paradas); return rq ? (
        <div className="bg-cyan-500/5 border border-cyan-500/30 rounded-md p-2 mb-2 text-[11px] text-cyan-100 flex flex-wrap gap-x-3 gap-y-0.5" data-testid={`grupo-${g.id}-requisitos`}>
          <span className="font-semibold">Requisitos (informativo):</span>
          {rq.nPersonal > 0 && <span data-testid={`grupo-${g.id}-req-personal`}>👥 {rq.nPersonal} trabajo(s) requieren {rq.personasMax} personas</span>}
          {rq.medios.length > 0 && <span data-testid={`grupo-${g.id}-req-medios`}>🪜 Medios: {rq.medios.map((m) => `${m.label} (${m.n})`).join(", ")}</span>}
          {rq.dificiles > 0 && <span>⚠ {rq.dificiles} de dificultad alta/especial</span>}
        </div>
      ) : null; })()}
      {g.advertencias?.length > 0 && (
        <div className="bg-zinc-950/50 rounded-md p-2 mb-2 text-[11px] space-y-0.5" data-testid={`grupo-${g.id}-advertencias`}>
          {g.advertencias.slice(0, 5).map((a, k) => <div key={k} className="flex items-start gap-1.5"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0 text-amber-300" />{a}</div>)}
          {g.advertencias.length > 5 && <div className="opacity-60">…y {g.advertencias.length - 5} más</div>}
        </div>
      )}
      <button type="button" onClick={() => setVerParadas((v) => !v)} className="text-[10px] uppercase tracking-wider opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-1">
        {verParadas ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Paradas
      </button>
      {verParadas && (
        <ol className="text-xs space-y-1.5 min-h-[40px]" data-testid={`grupo-${g.id}-paradas`}>
          {g.paradas.length === 0 && <li className="text-[11px] italic opacity-60 border border-dashed border-zinc-700 rounded p-3 text-center">Ruta vacía · arrastra aquí paradas</li>}
          {g.paradas.map((p, idx) => <ParadaItem key={p.linea_id} p={p} idx={idx} g={g} grupos={grupos} fechaPlan={fechaPlan} acciones={acciones} bloqueN={bloqueDe(p)} />)}
        </ol>
      )}
    </div>
  );
}
